import React, { useState } from 'react';
import {
	Box,
	Typography,
	useTheme,
	useMediaQuery,
	SwipeableDrawer,
	Drawer,
	IconButton,
	Tooltip,
	Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Footer from '../Sections/Footer';
import {
	getFrequencyUnit,
	dateFormat,
	capitalize,
} from '../../hepler/functions';
import { useTranslation } from '../../hooks/useTranslation';
import { keyboardFocusVisibleSx } from '../../utils/a11y.utils';

const RenewConsentDetails = (props) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTouchDevice = useMediaQuery('(hover: none)');
	const consent = props.consentData;
	const [openDrawer, setOpenDrawer] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [tooltipField, setTooltipField] = useState(null);

	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const handleFieldClick = (field) => (event) => {
		if (isTouchDevice) {
			setOpenDrawer(field);
		}
	};

	const handleCloseDrawer = () => {
		setOpenDrawer(null);
	};

	const getFieldContent = (field) => {
		const fiuName = global.entityName;
		const baseKey = `consentRenewal.tooltips.${field}`;

		return {
			title: t(`${baseKey}.title`),
			description: t(`${baseKey}.description`).replace('{fiuName}', fiuName),
		};
	};

	const infoLabelButtonSx = {
		fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.875rem' },
		fontWeight: 400,
		fontFamily: props?.customDetails?.fontFamily,
		color: '#666',
		borderBottom: '2px dotted #666',
		paddingBottom: '1px',
		display: 'inline-block',
		lineHeight: 1,
		cursor: 'pointer',
		background: 'none',
		border: 'none',
		padding: '0.25rem',
		borderRadius: '4px',
		...keyboardFocusVisibleSx,
	};

	const renderInfoLabel = (field, labelText, sx = {}) => {
		const content = getFieldContent(field);

		return (
			<Tooltip title={isTouchDevice ? '' : content.description} arrow>
				<Typography
					component="button"
					type="button"
					onClick={handleFieldClick(field)}
					aria-label={`${labelText}. ${content.description}`}
					sx={{ ...infoLabelButtonSx, ...sx }}
				>
					{labelText}
				</Typography>
			</Tooltip>
		);
	};

	const renderDrawer = () => {
		if (!openDrawer) return null;
		const content = getFieldContent(openDrawer);

		return (
			<SwipeableDrawer
				anchor="bottom"
				open={Boolean(openDrawer)}
				onClose={handleCloseDrawer}
				onOpen={() => {}}
				sx={{
					'& .MuiDrawer-paper': {
						borderTopLeftRadius: '20px',
						borderTopRightRadius: '20px',
						maxHeight: '50vh',
						backgroundColor: isDark
							? '#161616'
							: props.customDetails?.secondaryColor || '#fff',
					},
				}}
			>
				<Box
					sx={{
						backgroundColor: isDark
							? '#161616'
							: props.customDetails?.secondaryColor || '#fff',
						height: '100%',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<Box
						sx={{
							flex: 1,
							overflowY: 'auto',
							padding: '10px 20px 20px 20px',
						}}
					>
						<Box sx={{ marginBottom: '10px' }}>
							<Typography
								variant="h6"
								sx={{
									fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
									fontWeight: 600,
									fontFamily: props?.customDetails?.fontFamily,
									color: isDark ? '#fff' : '#000',
								}}
							>
								{content.title}
							</Typography>
						</Box>

						<Typography
							sx={{
								fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.875rem' },
								fontFamily: props?.customDetails?.fontFamily,
								color: isDark ? '#ccc' : '#666',
								lineHeight: 1.6,
								marginBottom: '0px',
							}}
						>
							{content.description}
						</Typography>
					</Box>

					<Box
						sx={{
							padding: '16px 20px',
						}}
					>
						<Button
							variant="outlined"
							fullWidth
							onClick={handleCloseDrawer}
							sx={{
								borderColor: props.customDetails?.primaryColor || '#4A5EAD',
								color: props.customDetails?.primaryColor || '#4A5EAD',
								textTransform: 'none',
								fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
								fontWeight: 500,
								fontFamily: props.customDetails?.fontFamily,
								padding: '12px 24px',
								borderRadius: '8px',
								backgroundColor: props.customDetails?.secondaryColor || '#fff',
								'&:hover': {
									borderColor: props.customDetails?.primaryColor || '#4A5EAD',
									backgroundColor: 'rgba(0,0,0,0.04)',
								},
							}}
						>
							{t('consentRenewal.buttons.understood')}
						</Button>
					</Box>
					<Footer customDetails={props.customDetails} />
				</Box>
			</SwipeableDrawer>
		);
	};

	const getConsentValidityDuration = (startTime, expireTime) => {
		if (!startTime || !expireTime) return '';

		const start = new Date(startTime);
		const end = new Date(expireTime);

		// Calculate the difference in years, months, and days using calendar arithmetic
		let years = end.getFullYear() - start.getFullYear();
		let months = end.getMonth() - start.getMonth();
		let days = end.getDate() - start.getDate();

		// Handle negative days by borrowing from months
		// e.g., Jan 20 to Feb 15 = 26 days (not -5 days)
		if (days < 0) {
			months--;
			// Get the last day of the previous month to handle varying month lengths
			const lastDayOfPrevMonth = new Date(
				end.getFullYear(),
				end.getMonth(),
				0
			).getDate();
			days += lastDayOfPrevMonth;
		}

		// Handle negative months by borrowing from years
		// e.g., May 2024 to Feb 2025 = 9 months (not -3 months)
		if (months < 0) {
			years--;
			months += 12;
		}

		// Build human-readable string with singular/plural forms
		const parts = [];
		if (years > 0) {
			parts.push(years === 1 ? '1 year' : `${years} years`);
		}
		if (months > 0) {
			parts.push(months === 1 ? '1 month' : `${months} months`);
		}
		if (days > 0) {
			parts.push(days === 1 ? '1 day' : `${days} days`);
		}

		return parts.join(' ') || '0 Days';
	};

	return (
		<Box
			sx={{
				fontFamily: props?.customDetails?.fontFamily,
				paddingTop: '10px',
				paddingLeft: isMobile ? '20px' : '30px',
				paddingRight: isMobile ? '20px' : '30px',
				paddingBottom: 0,
			}}
		>
			<Box sx={{ marginBottom: { xs: '8px', sm: '20px' }, textAlign: 'left' }}>
				{renderInfoLabel('reason', t('consentRenewal.labels.reason'))}
				<Typography
					sx={{
						fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
						fontWeight: 500,
						fontFamily: props?.customDetails?.fontFamily,
						color: '#000',
						marginTop: { xs: '8px', sm: '12px', md: '6px' },
						lineHeight: 1.5,
						textAlign: 'left',
					}}
				>
					{consent?.Purpose?.text}
				</Typography>
			</Box>

			<Box
				sx={{
					display: 'flex',
					gap: isMobileOrTablet ? '6px' : '20px',
					justifyContent: 'flex-start',
					marginBottom: { xs: '8px', sm: '20px' },
					flexWrap: 'nowrap',
				}}
			>
				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						position: 'relative',
						paddingRight: isMobileOrTablet ? '6px' : '20px',
						textAlign: 'left',
						'&::after': {
							content: '""',
							position: 'absolute',
							right: 0,
							top: '30%',
							height: '40%',
							width: '1px',
							backgroundColor: '#999',
						},
					}}
				>
					{renderInfoLabel('dataRange', t('consentRenewal.labels.dataRange'), {
						marginBottom: { xs: '8px', sm: '12px', md: '6px' },
						whiteSpace: 'nowrap',
					})}
					<Typography
						sx={{
							fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
							fontWeight: 500,
							fontFamily: props?.customDetails?.fontFamily,
							color: '#000',
						}}
					>
						{getConsentValidityDuration(
							consent?.DataDateTimeRange?.from,
							consent?.DataDateTimeRange?.to
						)}
					</Typography>
				</Box>

				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						position: 'relative',
						paddingLeft: isMobileOrTablet ? '6px' : '20px',
						paddingRight: isMobileOrTablet ? '6px' : '20px',
						textAlign: 'left',
						'&::after': {
							content: '""',
							position: 'absolute',
							right: 0,
							top: '30%',
							height: '40%',
							width: '1px',
							backgroundColor: '#999',
						},
					}}
				>
					{renderInfoLabel('validFor', t('consentRenewal.labels.canViewTill'), {
						marginBottom: { xs: '8px', sm: '12px', md: '6px' },
						whiteSpace: 'nowrap',
					})}
					<Typography
						sx={{
							fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
							fontWeight: 500,
							fontFamily: props?.customDetails?.fontFamily,
							color: '#000',
						}}
					>
						{getConsentValidityDuration(
							consent?.startTime,
							consent?.expireTime
						)}
					</Typography>
				</Box>

				<Box
					sx={{
						flex: 1,
						minWidth: 0,
						paddingLeft: isMobileOrTablet ? '6px' : '20px',
						textAlign: 'left',
					}}
				>
					{renderInfoLabel('storeData', t('consentRenewal.labels.storeDataFor'), {
						marginBottom: { xs: '8px', sm: '12px', md: '6px' },
						whiteSpace: 'nowrap',
					})}
					<Typography
						sx={{
							fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
							fontWeight: 500,
							fontFamily: props?.customDetails?.fontFamily,
							color: '#000',
						}}
					>
						{consent?.DataLife?.value} {consent?.DataLife?.unit?.toLowerCase()}
					</Typography>
				</Box>
			</Box>

			<Box sx={{ marginBottom: { xs: '8px', sm: '20px' }, textAlign: 'left' }}>
				{renderInfoLabel('frequency', t('consentRenewal.labels.viewData'))}
				<Typography
					sx={{
						fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
						fontWeight: 500,
						fontFamily: props?.customDetails?.fontFamily,
						color: '#000',
						marginTop: { xs: '8px', sm: '12px', md: '6px' },
						lineHeight: 1.5,
						textAlign: 'left',
					}}
				>
					{consent?.fetchType === 'ONETIME'
						? 'ONE TIME'
						: `upto ${consent?.Frequency?.value} times ${getFrequencyUnit(
								consent?.Frequency?.unit.toLowerCase()
						  )}`}
				</Typography>
			</Box>
			<hr />
			{renderDrawer()}
		</Box>
	);
};

export default RenewConsentDetails;
